import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Button, Form, Input, Switch, Modal, notification, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
    getResourcesCategories,
    createResourcesCategories,
} from "../../../service";
import { ResourceCategorySection } from "../../molecules/menu-resource-category-section/ResourceCategorySection";
import { colors, spacing } from "../../../styles/theme";

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${spacing.xl};
`;

const TabDescription = styled.p`
  font-size: 14px;
  color: ${colors.textSecondary};
  margin: 0;
  max-width: 600px;
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

export const ResourcesTab: React.FC = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [categories, setCategories] = useState<any[]>([]);
    const [newCategoryId, setNewCategoryId] = useState<number | null>(null);
    const categoryRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

    const { isLoading, data: queryData } = useQuery<any, Error>(
        ["/resource-categories"],
        () => getResourcesCategories(),
        {
            onSuccess(data) {
                setCategories(data);
            },
        }
    );

    // Sync local state with query cache
    useEffect(() => {
        if (queryData && Array.isArray(queryData)) {
            setCategories((currentCategories) => {
                if (currentCategories.length === 0) {
                    return queryData;
                }
                const currentIds = currentCategories.map((c) => c.id);
                const updatedCategories = currentCategories.map((cat) => {
                    const updated = queryData.find((q: any) => q.id === cat.id);
                    return updated ? { ...cat, ...updated } : cat;
                });
                const filteredCategories = updatedCategories.filter((cat) =>
                    queryData.some((q: any) => q.id === cat.id)
                );
                const newCategories = queryData.filter(
                    (q: any) => !currentIds.includes(q.id)
                );
                return [...filteredCategories, ...newCategories];
            });
        }
    }, [queryData]);

    const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
    const [addCategoryForm] = Form.useForm();

    const {
        mutateAsync: createCategoryMutation,
        isLoading: isCreatingCategory,
    } = useMutation(createResourcesCategories, {
        onSuccess: (data: any) => {
            notification.success({
                message: t("resourcesTab.categoryCreatedSuccess"),
                style: { background: colors.successBackground },
                placement: "bottomRight",
            });
            addCategoryForm.resetFields();
            setAddCategoryModalOpen(false);
            queryClient.invalidateQueries(["/resource-categories"]);

            if (data?.id) {
                setNewCategoryId(data.id);
            }
        },
        onError: (error: any) => {
            notification.error({
                message: t("resourcesTab.categoryCreateError"),
                description: error?.response?.data?.message || t("resourcesTab.tryAgain"),
                style: { background: colors.errorBackground },
                placement: "bottomRight",
            });
        },
    });

    // Scroll to newly created category
    useEffect(() => {
        if (newCategoryId && categoryRefs.current[newCategoryId]) {
            setTimeout(() => {
                categoryRefs.current[newCategoryId]?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                setNewCategoryId(null);
            }, 100);
        }
    }, [newCategoryId, categories]);

    const handleAddCategory = () => {
        setAddCategoryModalOpen(true);
    };

    const handleAddCategorySubmit = async () => {
        try {
            const values = await addCategoryForm.validateFields();
            await createCategoryMutation(values);
        } catch {
            // validation failed
        }
    };

    if (isLoading) {
        return <Spin spinning={true} />;
    }

    return (
        <>
            <TabHeader>
                <TabDescription>
                    {t("resourcesTab.tabDescription")}
                </TabDescription>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddCategory}
                    loading={isCreatingCategory}
                >
                    {t("resourcesTab.addCategoryButton")}
                </Button>
            </TabHeader>

            <CategoriesContainer>
                {categories.map((category) => (
                    <div
                        key={category.id}
                        ref={(el) => (categoryRefs.current[category.id] = el)}
                    >
                        <ResourceCategorySection category={category} />
                    </div>
                ))}
            </CategoriesContainer>

            <Modal
                title={t("resourcesTab.addCategoryModalTitle")}
                open={addCategoryModalOpen}
                onOk={handleAddCategorySubmit}
                onCancel={() => {
                    addCategoryForm.resetFields();
                    setAddCategoryModalOpen(false);
                }}
                okText={t("resourcesTab.createButton")}
                cancelText={t("resourcesTab.cancelButton")}
                confirmLoading={isCreatingCategory}
                width={480}
            >
                <Form form={addCategoryForm} layout="vertical" autoComplete="off">
                    <Form.Item
                        name="name"
                        label={t("resourcesTab.categoryNameLabel")}
                        rules={[{ required: true, message: t("resourcesTab.categoryNameRequired") }]}
                    >
                        <Input placeholder={t("resourcesTab.categoryNamePlaceholder")} autoFocus />
                    </Form.Item>
                    <Form.Item
                        name="visibleForClient"
                        label={t("resourcesTab.visibleForClientLabel")}
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};
